import {
  Component,
  computed,
  inject,
  Input,
  NgZone,
  ViewEncapsulation,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { concatMap, delay, filter, tap } from 'rxjs/operators';
import { publicMethods } from '../../../globals';
import { MessageService } from '../../../services/message.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { I18nPluralPipe } from '@angular/common';
import { pluralMappings } from '../../../classes/i18n';

declare var $: any;

@Component({
    selector: 'export-to-crm',
    templateUrl: './export-to-crm.component.html',
    styleUrl: './export-to-crm.component.css',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ExportToCrmComponent {
  @Input() crmName: string;
  @Input() randomCompanyIds: string[];

  ngZone = inject(NgZone);
  dataStore = inject(DataSharingService);
  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  snackbar = inject(MatSnackBar);
  pluralPipe = inject(I18nPluralPipe);

  exporterLoading = computed(() => {
    const { isLoadingExporter, isLoadingWizardStepAction } =
      this.crmService.wizardLoading();

    return isLoadingExporter || isLoadingWizardStepAction;
  });

  pluralMappings = pluralMappings;
  MaxSelectedRecords = 1000;

  companyIds: string[] = [];
  searchQueryParams: Record<string, any> = {};
  crmObjectId: number = null;

  foundMatchedList: Record<string, any>[] = [];
  noMatchingList: Record<string, any>[] = [];

  allExistingChecked = false;
  allNewChecked = false;

  newlyCreatedRecordsDict: Record<string, any> = {};
  isCreatingRecords = false;

  ngOnInit(): void {
    $('#exportToCRMModal').on('show.bs.modal', e => {
      this.ngZone.run(() => this.getCompanyIdsOrParams());
    });
    $('#exportToCRMModal').on('hide.bs.modal', e => {
      this.ngZone.run(() => this.wipeOldDataOnModalClose());
    });
  }

  get formattedCRMName(): string {
    if (this.crmName?.toLowerCase() === 'hubspot') return 'HubSpot';

    return this.crmName;
  }

  getCompanyIdsOrParams(): void {
    if (this.pubMethods.hasData(this.randomCompanyIds)) {
      this.companyIds = this.randomCompanyIds;
    } else {
      this.dataStore.currentSelectedCompanyIds.subscribe(companyIds => {
        this.companyIds = companyIds;
      });
    }

    this.dataStore.currentSelectedSearchParams.subscribe(searchParams => {
      if (!searchParams.updates) return;

      searchParams.updates?.forEach(paramObj => {
        const { param, value } = paramObj;
        this.searchQueryParams[param] = value;
      });
    });

    // call search endpoint
    this.getExportSettingsByObjectIdOrParams();
  }

  getExportSettingsByObjectIdOrParams(): void {
    this.crmService
      .getCRMExistingState({
        actionType: 'crm_object_type',
        crmName: this.crmName.toLowerCase(),
      })
      .pipe(
        tap(objectData => {
          if (this.pubMethods.hasData(objectData) && objectData.length) {
            this.crmObjectId = objectData[0].id;
            this.MaxSelectedRecords = objectData[0].maxRecordsPerRequest ?? 1000;
          } else {
            this.messageService.show(
              'Something went wrong, please refresh and try again. If issue persists, please contact us. {crmObjectId}'
            );
          }
        }),
        filter(() => this.pubMethods.hasData(this.crmObjectId)),
        concatMap(() => {
          return this.crmService.getCRMExportSettings({
            objectId: this.crmObjectId,
            companyIds: this.companyIds,
            crmName: this.crmName,
            searchQParams: this.searchQueryParams,
          });
        }),
        tap(data => {
          if (data) {
            this.foundMatchedList = data.matches;
            this.handleInBetweenStateForNoMatching(data.nonMatching);
          }
        })
      )
      .subscribe();
  }

  handleInBetweenStateForNoMatching(
    nonMatchingData: Record<string, any>[]
  ): void {
    this.noMatchingList = nonMatchingData?.filter(record => {
      return !(record.ukCompanyNumber in this.newlyCreatedRecordsDict);
    });
  }

  toggleAllCheckboxes(existingOrNew: 'existing' | 'new') {
    if (existingOrNew === 'existing') {
      this.foundMatchedList.forEach(matchObj => {
        matchObj.checked = this.allExistingChecked;
      });
    }
    if (existingOrNew === 'new') {
      this.noMatchingList?.forEach(obj => {
        obj.checked = this.allNewChecked;
      });
    }
  }

  updateHubspotRecords(): void {
    const selectedCompanies = this.foundMatchedList.filter(obj => obj.checked);
    if (selectedCompanies?.length > this.MaxSelectedRecords) {
      this.messageService.showAndHideAfterDelay(
        `Maximum number of records to update must not exceed ${this.MaxSelectedRecords}!`,
        4000
      );
      return;
    }

    if (!selectedCompanies.length) {
      this.pubMethods.log('Please select one or more records.');
      return;
    }

    const recordIdToCompanyNumberObj = {};
    selectedCompanies.forEach(company => {
      const { crmRecordId, ukCompanyNumber } = company;
      recordIdToCompanyNumberObj[crmRecordId] = ukCompanyNumber;
    });

    const payloadObj = {
      crmRecordIdsToCompanyNumbers: recordIdToCompanyNumberObj,
    };

    this.crmService
      .updateCRMRecords({
        objectId: this.crmObjectId,
        crmName: this.crmName,
        payloadObj,
      })
      .subscribe(response => {
        if (response.ok && this.pubMethods.hasData(response.body)) {
          const { numberOfRecordsUpdated } = response.body;
          const pluralisedRecords = this.pluralPipe.transform(
            numberOfRecordsUpdated,
            pluralMappings.record
          );
          const successMsg = `${numberOfRecordsUpdated} ${pluralisedRecords} updated.`;
          this.snackbar.open(successMsg, 'OK', {
            duration: 3100,
          });
        }
      });
  }

  createHubspotRecords(): void {
    const selectedCompaniesToCreate = this.noMatchingList.filter(
      obj =>
        obj.checked && !(obj.ukCompanyNumber in this.newlyCreatedRecordsDict)
    );

    if (selectedCompaniesToCreate?.length > this.MaxSelectedRecords) {
      this.messageService.showAndHideAfterDelay(
        `Maximum number of records to create must not exceed ${this.MaxSelectedRecords}!`,
        4000
      );
      return;
    }
    if (!selectedCompaniesToCreate.length) {
      this.pubMethods.log('Please select one or more records.');
      return;
    }

    this.isCreatingRecords = true;

    const payloadObj = { companyNumbers: [] };
    selectedCompaniesToCreate.forEach(company => {
      payloadObj.companyNumbers.push(company.ukCompanyNumber);
    });

    this.crmService
      .createNewCRMRecords({
        crmName: this.crmName,
        objectId: this.crmObjectId,
        payloadObj,
      })
      .pipe(
        tap({
          next: (response) => {
            if (response.ok && this.pubMethods.hasData(response.body)) {
              const { created } = response.body;
              const pluralisedRecords = this.pluralPipe.transform(
                created?.length,
                pluralMappings.record
              );
              const successMsg = `${created?.length} ${pluralisedRecords} created.`;
              this.snackbar.open(successMsg, 'OK', { duration: 4000 });

              const createdRecordsMap = created.reduce(
                (acc: any, record: any) => {
                  acc[record.ukCompanyNumber] = record;
                  return acc;
                },
                {}
              );
              this.newlyCreatedRecordsDict = {
                ...this.newlyCreatedRecordsDict,
                ...createdRecordsMap,
              };

              this.noMatchingList = this.noMatchingList.filter(record => {
                return !selectedCompaniesToCreate.some(selected =>
                  selected.ukCompanyNumber === record.ukCompanyNumber
                );
              });
            }
          },
          error: () => {
            this.isCreatingRecords = false;
          },
          complete: () => {
            this.isCreatingRecords = false;
          }
        })
      )
      .subscribe();
  }

  wipeOldDataOnModalClose(): void {
    this.reset();

    if (document.activeElement) {
      (document.activeElement as any).blur();
    }
  }

  getCRMIcon(crmType: string): string {
    const fontAwesomeCRMs = new Set(['hubspot', 'salesforce']);

    if (fontAwesomeCRMs.has(crmType)) {
      return `<i class="fab fa-${crmType}"></i>`;
    }

    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  reset(): void {
    this.allExistingChecked = false;
    this.allNewChecked = false;
    this.companyIds = [];
    this.isCreatingRecords = false;
  }
}
